/**
 * Photo slot upload lifecycle shared by the gettingStartedPhoto and profilePhotoUploader Alpine components.
 * Spread into the component object. The host provides photos, albumId, uploadControllers, nextUploadId,
 * showError, addFileToSlot, the drag and replace state, the slot indexes in SLOT_INDEX_FIELDS that it uses,
 * and optionally clearSelection.
 */

import {
    uploadPhoto, deletePhoto, saveAlbumOrder, saveAlbumCover, parseUploadError, createEmptySlot
} from './photo-uploader.js';
import { t } from './i18n.js';

// Host fields that hold a slot index across an await, re-mapped when a removal shifts the slots.
const SLOT_INDEX_FIELDS = ['deleteConfirmSlot', 'replaceConfirmSlot', 'activeSlotIndex', 'selectedPhotoIndex'];

export const photoSlotUploads = {
    // The host builds the slot fields. This gives the slot its upload id and starts the upload.
    startSlotUpload(file, slotIndex, slot) {
        const uploadId = ++this.nextUploadId;
        slot.uploadId = uploadId;
        this.photos[slotIndex] = slot;
        this.doUpload(file, uploadId);
    },

    // Removing a lower slot shifts this one, so the slot is looked up by upload id after every await.
    slotForUpload(uploadId) {
        return this.photos.find(p => p.uploadId === uploadId) || null;
    },

    handleSlotDrop(event, slotIndex) {
        this.dragOverSlot = null;
        this.globalDragOver = false;

        const slot = this.photos[slotIndex];
        const file = event.dataTransfer?.files?.[0];
        if (slot.uploading || !file?.type?.startsWith('image/')) return;

        // A drop on an uploaded photo is a replace, so it takes the picker's confirm, order save and delete.
        if (slot.uploaded && slot.photoId) {
            this.pendingReplaceFile = file;
            this.replaceConfirmSlot = slotIndex;
            return;
        }
        this.addFileToSlot(file, slotIndex);
    },

    async doUpload(file, uploadId) {
        const controller = new AbortController();
        this.uploadControllers.set(uploadId, controller);

        try {
            const result = await uploadPhoto({
                file,
                albumId: this.albumId,
                controller,
                onProgress: (percent) => {
                    const slot = this.slotForUpload(uploadId);
                    if (slot) slot.progress = percent;
                }
            });

            const slot = this.slotForUpload(uploadId);
            if (!slot) return;
            slot.progress = 100;
            slot.photoId = String(result.id);

            // The slot stays uploading until the replaced photo is gone, so submit waits for it.
            if (slot.oldPhotoIdToDelete) {
                await this.finishReplace(slot.oldPhotoIdToDelete);
            }

            const finished = this.slotForUpload(uploadId);
            if (finished) {
                finished.oldPhotoIdToDelete = null;
                finished.uploading = false;
                finished.uploaded = true;
            }
        } catch (err) {
            // A failed replace keeps oldPhotoIdToDelete: the old photo stays in the album at this position.
            const slot = this.slotForUpload(uploadId);
            if (!slot) return;
            slot.uploading = false;

            const errorMsg = parseUploadError(err);
            if (errorMsg !== 'Upload cancelled') {
                slot.error = errorMsg;
            }
        } finally {
            this.uploadControllers.delete(uploadId);
        }
    },

    // Save the on-screen order before the delete, so the replacement keeps the replaced slot's
    // position and cover. A failed save keeps the original photo.
    async finishReplace(oldPhotoId) {
        if (!await this.persistPhotoOrder()) return;
        try {
            await deletePhoto(oldPhotoId);
        } catch {
            // The order is saved. A failed delete only leaves the old photo in the album.
        }
    },

    // Order and cover saves run one at a time, so each reads the slots when it starts and the last one wins.
    queueAlbumWrite(write) {
        const run = (this.albumWrites || Promise.resolve()).then(write);
        this.albumWrites = run.catch(() => {});
        return run;
    },

    // A replace that has no new photo keeps its old photo in its slot's position.
    async persistPhotoOrder() {
        const saved = await this.queueAlbumWrite(() => saveAlbumOrder({
            albumId: this.albumId,
            photoIds: this.photos.map(p => p.photoId || p.oldPhotoIdToDelete).filter(Boolean),
        }));
        if (!saved) this.showError(t('common.save_order_failed'));
        return saved;
    },

    async setAsPrimary(slotIndex) {
        // A replace in flight saves the album order, which would reset the cover picked here.
        if (this.photos.some(p => p.uploading && p.oldPhotoIdToDelete)) return;
        if (this.photos[slotIndex].uploading || this.photos[0].uploading) return;

        const photoId = this.photos[slotIndex].photoId;
        if (!photoId || !this.albumId) return;

        if (this.clearSelection) this.clearSelection();

        const primaryPhotoId = this.photos[0].photoId || this.photos[0].oldPhotoIdToDelete;
        const clickedPhoto = this.photos[slotIndex];
        this.photos[slotIndex] = this.photos[0];
        this.photos[0] = clickedPhoto;

        try {
            await this.queueAlbumWrite(() => saveAlbumCover({ albumId: this.albumId, photoId }));
        } catch (err) {
            // A replace during the request puts a different slot at an index, so the swap is undone by photo id.
            const clickedIndex = this.photos.findIndex(p => p.photoId === photoId);
            const primaryIndex = primaryPhotoId
                ? this.photos.findIndex(p => (p.photoId || p.oldPhotoIdToDelete) === primaryPhotoId)
                : -1;
            if (clickedIndex !== -1 && primaryIndex !== -1) {
                const clicked = this.photos[clickedIndex];
                this.photos[clickedIndex] = this.photos[primaryIndex];
                this.photos[primaryIndex] = clicked;
            }
            this.showError(err.message);
        }
    },

    async executeDelete() {
        const slotIndex = this.deleteConfirmSlot;
        if (slotIndex === null) return;

        const photoId = this.photos[slotIndex].photoId;
        this.deleteConfirmSlot = null;

        if (!photoId) {
            this.removePhotoLocally(slotIndex);
            return;
        }

        try {
            await deletePhoto(photoId);
        } catch (err) {
            this.showError(parseUploadError(err) || t('common.photo_delete_failed'));
            return;
        }

        // Removing a lower slot during the delete shifts this one, so the slot is looked up by photo id.
        const currentIndex = this.photos.findIndex(p => p.photoId === photoId);
        if (currentIndex !== -1) this.removePhotoLocally(currentIndex);
    },

    removePhotoLocally(slotIndex) {
        const uploadId = this.photos[slotIndex].uploadId;
        const controller = this.uploadControllers.get(uploadId);
        if (controller) {
            controller.abort();
            this.uploadControllers.delete(uploadId);
        }

        // Revoke blob URLs to prevent memory leaks (but not server URLs)
        const currentPreview = this.photos[slotIndex].preview;
        if (currentPreview && currentPreview.startsWith('blob:')) {
            URL.revokeObjectURL(currentPreview);
        }

        for (let i = slotIndex; i < this.photos.length - 1; i++) {
            this.photos[i] = this.photos[i + 1];
        }
        this.photos[this.photos.length - 1] = createEmptySlot();

        for (const field of SLOT_INDEX_FIELDS) {
            if (field in this) this[field] = indexAfterRemoval(this[field], slotIndex);
        }
    }
};

function indexAfterRemoval(index, removedIndex) {
    if (index === null || index === undefined || index === removedIndex) return null;
    return index > removedIndex ? index - 1 : index;
}
